import { apiRequest, getApiBaseUrl, getToken } from './apiRequest';
import type { ApiRequestError } from '../../types/api/envelope';
import { normalizeApiResponse } from '../../utils/responseBuilder';

/** Unwrap a non-streaming /api/ai/chat JSON body (e.g. scope clarification). */
export async function parseChatJsonResponse(response: Response) {
  const raw = (await response.json()) as Record<string, any>;
  if (!response.ok) {
    const normalized = normalizeApiResponse(raw);
    const message =
      normalized.message ||
      raw.error ||
      raw.message ||
      `Request failed with status ${response.status}`;
    const err = new Error(message) as ApiRequestError;
    err.code =
      raw.code ||
      (normalized.success === false ? normalized.code : undefined) ||
      (raw.data && raw.data.code);
    err.meta =
      (normalized.success !== false ? normalized.meta : undefined) || raw.meta || undefined;
    throw err;
  }
  const normalized = normalizeApiResponse(raw);
  const data =
    normalized.success !== false && normalized.data !== undefined
      ? normalized.data
      : normalized;
  if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
    const record = data as Record<string, any>;
    const meta =
      normalized.success !== false && normalized.meta ? normalized.meta : undefined;
    return {
      ...record,
      meta: { ...(record.meta || {}), ...(meta || {}) },
    };
  }
  return data;
}

export const aiAPI = {
  chat: async (payload) => {
    return await apiRequest<any>('/api/ai/chat', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  chatStream: async (payload, signal) => {
    const base = getApiBaseUrl();
    const token = getToken();
    return fetch(`${base}/api/ai/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      signal,
    });
  },
  insights: async ({ forceRefresh = false }: { forceRefresh?: boolean } = {}) => {
    return await apiRequest<any>('/api/ai/insights', {
      method: 'POST',
      body: JSON.stringify(forceRefresh ? { forceRefresh: true } : {}),
    });
  },
  status: async () => {
    return await apiRequest<any>('/api/ai/status', { method: 'GET' });
  },
  scopeCatalog: async ({ q = '', limit = 30 } = {}, signal) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    return await apiRequest<any>(`/api/ai/scope-catalog${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      signal,
    });
  },
};
